import {
  BLOCK_FIXTURES,
  getDefaultFixtureForComponent,
  MATRIX_LOCALES,
  PAGE_FIXTURES,
} from '@web-studio/core-fixtures';
import { THEME_ID_KEYS, type RegistryBlockKey, type ThemeId } from '@web-studio/shared-schemas/catalog';
import type { LayoutComponentType } from '@web-studio/shared-schemas/content';
import {
  isValidThemeColorSet,
  SPACING_ENUM_KEY_VALUES,
  THEME_REGISTRY,
  type SpacingEnumKey,
} from '@web-studio/shared-schemas/theme';
import {
  implementedBlockCatalog,
  IMPLEMENTED_COMPONENT_TYPES,
  registryOnlyBlockKeys,
  verticalsForRegistryKey,
} from './blockCatalog';
import { buildMatrixCellId, buildPageCellId } from './cellId';
import type { ImplementationStatus, MatrixCell, PageMatrixCell } from './types';

function themeAppliesToVerticals(
  themeId: ThemeId,
  verticals: readonly string[],
): boolean {
  const entry = THEME_REGISTRY[themeId];
  if (!entry || verticals.length === 0) {
    return true;
  }
  return entry.allowed_verticals.some((v) => verticals.includes(v));
}

export interface EnumerateBlockMatrixOptions {
  /** When set, only cells for this component type. */
  componentType?: LayoutComponentType;
  themeId?: string;
  colorsetId?: string;
  spacingEnum?: SpacingEnumKey;
  locale?: (typeof MATRIX_LOCALES)[number];
  fixtureId?: string;
  /** Include registry-only (unimplemented) blocks. Default true. */
  includeRegistryOnly?: boolean;
}

export function enumerateBlockThemeMatrix(
  options: EnumerateBlockMatrixOptions = {},
): MatrixCell[] {
  const includeRegistryOnly = options.includeRegistryOnly ?? true;
  const cells: MatrixCell[] = [];

  const catalog = implementedBlockCatalog().filter(
    (row) => !options.componentType || row.componentType === options.componentType,
  );

  for (const row of catalog) {
    const defaultFixture = getDefaultFixtureForComponent(row.componentType);
    const fixtureId = options.fixtureId ?? defaultFixture?.id ?? 'missing';
    const status: ImplementationStatus =
      defaultFixture && BLOCK_FIXTURES.some((f) => f.id === fixtureId)
        ? 'rendered'
        : 'missing_fixture';

    for (const themeId of THEME_ID_KEYS) {
      if (options.themeId && themeId !== options.themeId) continue;
      if (!themeAppliesToVerticals(themeId, row.verticals)) continue;

      const colorSets = THEME_REGISTRY[themeId]?.color_sets ?? [];
      for (const colorSet of colorSets) {
        if (options.colorsetId && colorSet.id !== options.colorsetId) continue;
        if (!isValidThemeColorSet(themeId, colorSet.id)) continue;

        for (const spacingEnum of SPACING_ENUM_KEY_VALUES) {
          if (options.spacingEnum && spacingEnum !== options.spacingEnum) continue;

          for (const locale of MATRIX_LOCALES) {
            if (options.locale && locale !== options.locale) continue;

            cells.push({
              cellId: buildMatrixCellId({
                componentType: row.componentType,
                themeId,
                colorsetId: colorSet.id,
                spacingEnum,
                locale,
                fixtureId,
              }),
              componentType: row.componentType,
              registryKey: row.registryKey,
              themeId,
              colorsetId: colorSet.id,
              spacingEnum,
              locale,
              fixtureId,
              implementationStatus: status,
              verticals: row.verticals,
            });
          }
        }
      }
    }
  }

  if (includeRegistryOnly) {
    for (const registryKey of registryOnlyBlockKeys()) {
      const verticals = verticalsForRegistryKey(registryKey as RegistryBlockKey);
      for (const themeId of THEME_ID_KEYS) {
        if (options.themeId && themeId !== options.themeId) continue;
        if (!themeAppliesToVerticals(themeId, verticals)) continue;

        const colorSets = THEME_REGISTRY[themeId]?.color_sets ?? [];
        for (const colorSet of colorSets) {
          if (options.colorsetId && colorSet.id !== options.colorsetId) continue;

          for (const spacingEnum of SPACING_ENUM_KEY_VALUES) {
            if (options.spacingEnum && spacingEnum !== options.spacingEnum) continue;

            for (const locale of MATRIX_LOCALES) {
              if (options.locale && locale !== options.locale) continue;

              cells.push({
                cellId: buildMatrixCellId({
                  componentType: registryKey,
                  themeId,
                  colorsetId: colorSet.id,
                  spacingEnum,
                  locale,
                  fixtureId: 'n/a',
                }),
                componentType: registryKey,
                registryKey,
                themeId,
                colorsetId: colorSet.id,
                spacingEnum,
                locale,
                fixtureId: 'n/a',
                implementationStatus: 'registry_only',
                verticals,
              });
            }
          }
        }
      }
    }
  }

  return cells;
}

export interface EnumeratePageMatrixOptions {
  pageFixtureId?: string;
  themeId?: string;
  colorsetId?: string;
  spacingEnum?: SpacingEnumKey;
  locale?: (typeof MATRIX_LOCALES)[number];
}

export function enumeratePageLayoutMatrix(
  options: EnumeratePageMatrixOptions = {},
): PageMatrixCell[] {
  const cells: PageMatrixCell[] = [];

  for (const page of PAGE_FIXTURES) {
    if (options.pageFixtureId && page.id !== options.pageFixtureId) continue;

    for (const themeId of THEME_ID_KEYS) {
      if (options.themeId && themeId !== options.themeId) continue;
      if (!themeAppliesToVerticals(themeId, [page.vertical])) continue;

      const colorSets = THEME_REGISTRY[themeId]?.color_sets ?? [];
      for (const colorSet of colorSets) {
        if (options.colorsetId && colorSet.id !== options.colorsetId) continue;
        if (!isValidThemeColorSet(themeId, colorSet.id)) continue;

        for (const spacingEnum of SPACING_ENUM_KEY_VALUES) {
          if (options.spacingEnum && spacingEnum !== options.spacingEnum) continue;

          for (const locale of MATRIX_LOCALES) {
            if (options.locale && locale !== options.locale) continue;

            cells.push({
              cellId: buildPageCellId({
                pageFixtureId: page.id,
                themeId,
                colorsetId: colorSet.id,
                spacingEnum,
                locale,
              }),
              pageFixtureId: page.id,
              vertical: page.vertical,
              tier: page.tier,
              themeId,
              colorsetId: colorSet.id,
              spacingEnum,
              locale,
              blockCount: page.blocks.length,
            });
          }
        }
      }
    }
  }

  return cells;
}

export function getRenderedBlockCells(): MatrixCell[] {
  return enumerateBlockThemeMatrix({ includeRegistryOnly: false }).filter(
    (c) => c.implementationStatus === 'rendered',
  );
}

export function listImplementedComponentTypes(): LayoutComponentType[] {
  return [...IMPLEMENTED_COMPONENT_TYPES];
}
